import axios from 'axios';
import moment from 'moment';

export type JobRef = {
  id: string;
  name: string;
  location?: string;
  url?: string;
};

export type CandidateRef = {
  id: string;
  ats_id?: string;
}

export type InterviewRef = {
  id: string;
  ats_id?: string;
}

export type StageRef = {
  id: string;
  name: string;
  order: number;
  is_required?: boolean;
  is_interview?: boolean;
};

type WithStages = {
  stages: StageRef[];
};

export type AtsStatus = {
  state: 'NOT_CONNECTED' | 'AWAITING_INITIAL_SYNC' | 'CONNECTED';
  tool_name: string;
  last_sync_date?: Date;
  ats_jobs: JobRef[];
  job_connections: {
    ats_job: JobRef;
    local_job?: JobRef;
  }[];
};

export type AtsJobStatus = {
  local_job: JobRef & WithStages;
  ats_job?: JobRef & WithStages;
  stage_mappings: {
    ats_stage: StageRef;
    local_stage?: StageRef;
  }[];
};

export type AtsCandidateStatus = {
  local_candidate: CandidateRef;
  ats_candidate: CandidateRef;
  interview: InterviewRef;
}

export const getConnectLink = async (options?: {
  environment?: string;
  tool?: string;
  hostname?: string;
}) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}integrations/ats/connection/link/`,
    { options }
  );
  return response.data.link;
};

export const getSyncStatus = async () => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}integrations/ats/connection/sync-status/`
  );
  return response.data as { awaiting_initial_sync: boolean };
};

export const activateConnection = async (token: string) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}integrations/ats/connection/activate/`,
    { token }
  );
  return response.data;
};

export const forceSync = async () => {
  // will throw if problem
  await axios.post(`${process.env.REACT_APP_API_URL}integrations/ats/sync/`);
};

type GetAtsStatusQuery = {
  post_url?: string,
  remote_ids?: string,
  job_codes?: string,
  name_contains?: string,
  statuses?: string,
};

export const ATS_JOB_FILTER_OPEN_JOBS: GetAtsStatusQuery = {
  statuses: 'OPEN',
};

export const getAtsStatus = async (jobFilters: GetAtsStatusQuery = {}) => {
  const queryString = new URLSearchParams(jobFilters).toString()
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}integrations/ats/connection/?${queryString}`,
  );
  const result: AtsStatus = {
    ...response.data,
    last_sync_date: response.data.last_sync_date
      ? moment(response.data.last_sync_date).toDate()
      : response.data.last_sync_date,
  };
  return result;
};

/**
 * Connects an existing HirePort job to an ATS job, with no stage mappings
 * @param localJobId the id of the job in HirePort
 * @param remoteJobId the id of the job in the ATS broker
 */
export const connectJob = async (localJobId: string, remoteJobId: string) => {
  await axios.post(`${process.env.REACT_APP_API_URL}integrations/ats/jobs/`, {
    local_job: { id: localJobId },
    ats_job: { id: remoteJobId },
    stage_mappings: [],
  });
}

/**
 * Creates a new HirePort job which is connected to an ATS job,
 * and is created with automatic maximal stage mappings
 * @param remoteJobId the ide of the job in the ATS broker to connect to
 */
export const createConnectedJob = async (remoteJobId: string) => {
  await axios.post(`${process.env.REACT_APP_API_URL}integrations/ats/jobs/`, {
    ats_job: { id: remoteJobId },
  });
}

export const getAtsJobStatus = async (jobId: string) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}integrations/ats/jobs/${jobId}/`,
  );
  return response.data as AtsJobStatus;
};

export const findAtsJobsByRemoteId = async (remoteId: string) => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}integrations/ats/jobs/?remote_ids=${remoteId}`);
  return response.data as AtsJobStatus[];
}

export const getAtsCandidates = async (jobId: string) => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}integrations/ats/jobs/${jobId}/candidates/`);
  return response.data as AtsCandidateStatus[];
}

export const createStageMappings = async (
  jobId: string,
  mappings: { localId: string; atsId: string }[],
) => {
  await axios.post(
    `${process.env.REACT_APP_API_URL}integrations/ats/jobs/${jobId}/stages/`,
    mappings.map(({ localId, atsId }) => ({
      local_stage: { id: localId },
      ats_stage: { id: atsId },
    })),
  );
};

export const disconnectJob = async (jobId: string) => {
  await axios.delete(
    `${process.env.REACT_APP_API_URL}integrations/ats/jobs/${jobId}/`,
  );
};

export const disconnectAts = async () => {
  await axios.delete(
    `${process.env.REACT_APP_API_URL}integrations/ats/connection/`,
  );
};

export type DefaultStageMappingType = 'INTRODUCED' | 'INTERVIEW' | 'OFFERED' | 'HIRED' | 'REJECTED' | 'ON_HOLD';

export interface DefaultStageMapping {
  id?: string;
  provider: string;
  provider_name?: string;
  company_name?: string;
  external_name: string;
  external_stage_name: string;
  stage_type: DefaultStageMappingType | null;
  order: number;
  is_unmapped: boolean;
}

export const getDefaultStageMappings = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}integrations/ats/default-mappings/provider/`,
    );
    return response.data as DefaultStageMapping[];
  } catch (error) {
    console.error('Error fetching default stage mappings:', error);
    return []; // Return empty array on error
  }
};

export const createDefaultStageMapping = async (mapping: DefaultStageMapping) => {
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}integrations/ats/default-mappings/`,
    mapping,
  );
  return response.data as DefaultStageMapping;
};

export const updateDefaultStageMapping = async (mapping: DefaultStageMapping) => {
  const response = await axios.put(
    `${process.env.REACT_APP_API_URL}integrations/ats/default-mappings/${mapping.id}/`,
    mapping,
  );
  return response.data as DefaultStageMapping;
};

export const deleteDefaultStageMapping = async (id: string) => {
  await axios.delete(
    `${process.env.REACT_APP_API_URL}integrations/ats/default-mappings/${id}/`,
  );
};

export const bulkUpdateDefaultStageMappings = async (mappings: Partial<DefaultStageMapping>[]) => {
  try {
    // Process mappings to ensure stage_type is always a valid value for the backend
    const processedMappings = mappings.map(mapping => ({
      ...mapping,
      // If is_unmapped is true, provide a default stage_type to satisfy backend requirements
      // The backend will still treat it as unmapped because of the is_unmapped flag
      stage_type: mapping.is_unmapped ? 'INTERVIEW' : (mapping.stage_type || 'INTERVIEW'),
      // Ensure order is always a number for consistent backend handling
      order: mapping.order ?? 0
    }));

    // Send all mappings in a single API request
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}integrations/ats/default-mappings/bulk-update/`,
      processedMappings
    );
    return response.data as DefaultStageMapping[];
  } catch (error) {
    console.error('Error updating default stage mappings:', error);
    throw error;
  }
};

export const deleteAllDefaultStageMappings = async () => {
  try {
    // Instead of deleting one by one, we'll let the bulk update endpoint handle deletion
    // This is just a fallback for when the bulk update isn't used
    const mappings = await getDefaultStageMappings();
    if (mappings.length === 0) {
      return;
    }

    // If there are fewer than 10 mappings, delete them one by one
    // This keeps code compatible with older endpoints
    if (mappings.length < 10) {
      await Promise.all(
        mappings.map(async (mapping) => {
          if (mapping.id) {
            try {
              await deleteDefaultStageMapping(mapping.id);
            } catch (error) {
              console.error(`Failed to delete mapping ${mapping.id}:`, error);
              // Continue with other deletions
            }
          }
        })
      );
    } else {
      // For larger sets, use bulk update with an empty array to effectively delete all
      await bulkUpdateDefaultStageMappings([]);
    }
  } catch (error) {
    console.error('Error deleting default stage mappings:', error);
    throw error; // Re-throw so caller can handle the error
  }
};

// Add new function to lookup job by ATS job ID directly from database
export const findJobByAtsJobId = async (atsJobId: string) => {
  const response = await axios.get(`${process.env.REACT_APP_API_URL}integrations/ats/jobs/by-ats-id/${atsJobId}/`);
  return response.data as AtsJobStatus;
}
